import { useQuery } from "@tanstack/react-query";
import React from "react";
import styled from "styled-components";

import { GraphQLAPI } from "@examspark/api";
import { useAuthorizationContext, useAppContext } from "@examspark/common-app";
import {
  Text,
  CautionBanner,
  Modal,
  ModalProps,
  VSpace,
} from "@examspark/common-ui";
import { PaddedBody } from "@examspark/common-ui/dist/components/modals/Modal";
import { WorkspaceRegistrationCode } from "@examspark/types";

const StyledCautionBanner = styled(CautionBanner)`
  border: solid 1px #e3e39b;
  border-radius: 0.5rem;
  background-color: #f6f6de;
`;

const StyledBody = styled(Text.Body)`
  color: #222;
`;

const StyledCenteredCode = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StyledLabel = styled.div`
  color: #666;
`;

const StyledCode = styled.div`
  padding: 1rem;
  border: solid 1px #ddd;
  font-size: 2rem;
  border-radius: 0.5rem;
  text-align: center;
`;

const WorkspaceRegistrationCodesModal = (props: ModalProps) => {
  const [context] = useAppContext();
  const authorizationContext = useAuthorizationContext();
  const { data } = useQuery({
    queryKey: ["workspace-registration-code", context.workspace?.id],
    queryFn: async () => {
      const result = await GraphQLAPI.query<{
        listWorkspaceRegistrationCodes: WorkspaceRegistrationCode[];
      }>({
        query: /* GraphQL */ `
          query ListWorkspaceRegistrationCodes(
            $authorizationContext: AuthorizationContextInput!
          ) {
            listWorkspaceRegistrationCodes(
              authorizationContext: $authorizationContext
            ) {
              id
              sk
              workspaceId
              schemaVersion
              createdAt
              creatorId
              updatedAt
              role
              __typename
            }
          }
        `,
        variables: {
          authorizationContext,
        },
      });

      return result.data?.listWorkspaceRegistrationCodes.find(
        (reg) => reg.role === "STANDARD",
      );
    },
  });

  // console.log("data", data);

  const code = data?.sk.split("#")[1];

  return (
    <Modal title="Registration Code" size="small" {...props}>
      <PaddedBody>
        {data ? (
          <VSpace>
            <StyledBody>
              Users may register for this workspace using the following
              Workspace ID and Registration Code.{" "}
              {/*  This registration code will
              regenerate every 24 hours. */}
            </StyledBody>
            <StyledCautionBanner>
              ESC's, Districts, and Campuses reside in different workspaces.
              Please ensure the code you are sharing corresponds to the correct
              ESC, Campus, or District the user should be associated with.
            </StyledCautionBanner>
            <div>
              <StyledCenteredCode>
                <StyledLabel>Workspace Name:</StyledLabel>
                <StyledCode>{context.workspace?.name}</StyledCode>
                <StyledLabel>Workspace ID:</StyledLabel>
                <StyledCode>{data.workspaceId}</StyledCode>
                <StyledLabel>Registration Code:</StyledLabel>
                <StyledCode>{code}</StyledCode>
              </StyledCenteredCode>
            </div>
          </VSpace>
        ) : (
          <StyledBody>
            Registration codes for this workspace could not be located at this
            time. Please contact your Service Center for assistance.
          </StyledBody>
        )}
      </PaddedBody>
    </Modal>
  );
};

export default WorkspaceRegistrationCodesModal;
